import { Component, effect, ElementRef, inject, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { provideTranslocoScope, TranslocoPipe } from '@jsverse/transloco';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideSearch, lucideX } from '@ng-icons/lucide';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { ManualSearchService } from './manual-search.service';

/**
 * Search box shared by the help panel and the `/manual` page. It talks to the
 * `ManualSearchService` its host provides: submitting runs the search, and an emptied box (the
 * clear button or the keyboard) resets it, which hides the results. The host empties it with
 * `reset()` when something else makes the results obsolete.
 */
@Component({
  selector: 'au-manual-search-box',
  imports: [ReactiveFormsModule, TranslocoPipe, NgIcon, HlmInputImports],
  providers: [provideTranslocoScope('manual'), provideIcons({ lucideSearch, lucideX })],
  template: `
    <form
      role="search"
      (submit)="submit($event)"
    >
      <!-- Same look as the list search (au-table-search), which searches as you type instead. -->
      <div class="relative w-full">
        <ng-icon
          name="lucideSearch"
          class="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2 text-sm"
        />
        <input
          #searchInput
          data-testid="manual-search-input"
          type="search"
          class="w-full pr-9 pl-9 [&::-webkit-search-cancel-button]:hidden"
          [attr.aria-label]="'manual.search.Label' | transloco"
          [formControl]="query"
          [placeholder]="'manual.search.Placeholder' | transloco"
          hlmInput
        />
        @if (queryValue()) {
          <button
            type="button"
            class="bg-muted-foreground/30 text-foreground hover:bg-muted-foreground/50 absolute top-1/2 right-3 flex size-5 -translate-y-1/2 items-center justify-center rounded-full text-xs transition-colors"
            [attr.aria-label]="'ClearSearch' | transloco"
            (click)="clear()"
          >
            <ng-icon name="lucideX" />
          </button>
        }
      </div>
    </form>
  `,
})
export class ManualSearchBox {
  private readonly search = inject(ManualSearchService);
  private readonly input = viewChild.required<ElementRef<HTMLInputElement>>('searchInput');
  protected readonly query = new FormControl('', { nonNullable: true });
  protected readonly queryValue = toSignal(this.query.valueChanges, { initialValue: '' });

  constructor() {
    effect(() => {
      if (this.queryValue() === '') untracked(() => this.search.reset());
    });
  }

  /** Empties the box without moving the focus; the emptied box hides the results by itself. */
  reset(): void {
    this.query.reset();
  }

  protected clear(): void {
    this.query.setValue('');
    this.input().nativeElement.focus();
  }

  protected submit(event: Event): void {
    event.preventDefault();
    this.search.search(this.query.value);
  }
}
